import axios from 'axios';
import { appConfig } from '../config/app.config';
import feathers from '../feathers';

const API_URL = `${appConfig.API_ENDPOINT}`;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  displayName?: string;
}

export interface AuthResponse {
  accessToken: string;
  user: {
    _id: string;
    email: string;
    displayName?: string;
    role: string;
    organizationId?: string;
    organizationRole?: string;
    stytchMemberId?: string;
  };
  organization?: {
    _id: string;
    name: string;
    slug: string;
    stytchOrganizationId: string;
  };
}

export interface StytchAuthResponse {
  sessionToken: string;
  user: {
    _id: string;
    email: string;
    displayName?: string;
    role: string;
    organizationId?: string;
    organizationRole?: string;
    stytchMemberId?: string;
  };
  organization?: {
    _id: string;
    name: string;
    slug: string;
    stytchOrganizationId: string;
  };
}

class AuthService {
  /**
   * Login with email magic link - Stytch Discovery flow
   * Sends magic link via backend which has access to Stytch B2B SDK
   */
  async loginWithMagicLink(email: string): Promise<void> {
    try {
      await axios.post(`${API_URL}/auth/magic-link`, {
        email,
        redirect_url: `${appConfig.FRONTEND_URL}/login`
      });
    } catch (error) {
      console.error('Magic link send failed:', error);
      throw error;
    }
  }

  /**
   * Authenticate with Stytch session token
   */
  async authenticateWithStytch(sessionToken: string): Promise<StytchAuthResponse> {
    try {
      console.log('🔐 AuthService: Starting authentication with Stytch session token...');

      // Authenticate with backend using Stytch session token
      console.log('📤 AuthService: Sending authentication request to backend...');
      const response = await axios.post(`${API_URL}/authentication`, {
        strategy: 'stytch',
        sessionToken
      });

      console.log('📥 AuthService: Backend response received:', {
        hasData: !!response.data,
        hasAccessToken: !!response.data?.accessToken,
        hasUser: !!response.data?.user,
        hasOrganization: !!response.data?.organization
      });

      if (response.data) {
        // Store session data
        console.log('💾 AuthService: Storing session data in localStorage...');
        localStorage.setItem('stytch_session', sessionToken);
        localStorage.setItem('user', JSON.stringify(response.data));

        // Authenticate Feathers client with the backend JWT token
        if (response.data.accessToken) {
          console.log('🔑 AuthService: Authenticating Feathers client...');
          await feathers.authenticate({
            strategy: 'jwt',
            accessToken: response.data.accessToken
          });
          console.log('✅ AuthService: Feathers authentication successful');
        }
      }

      console.log('✅ AuthService: Authentication complete, returning response');
      return response.data;
    } catch (error) {
      console.error('❌ AuthService: Authentication failed:', error);
      throw error;
    }
  }

  /**
   * Create organization and first member account
   */
  async createOrganization(data: {
    organizationName: string;
    organizationSlug: string;
    email: string;
    name: string;
  }): Promise<any> {
    try {
      // For now, this would need to be done via Stytch dashboard
      // or we need to implement a backend endpoint for this
      throw new Error('Organization creation via UI not yet implemented. Please use Stytch dashboard.');
    } catch (error) {
      console.error('Organization creation failed:', error);
      throw error;
    }
  }

  /**
   * Legacy login method - kept for backward compatibility
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await axios.post(`${API_URL}/authentication`, {
      strategy: 'local',
      ...credentials
    });

    if (response.data.accessToken) {
      localStorage.setItem('user', JSON.stringify(response.data));

      // Also authenticate the Feathers client with the token
      await feathers.authenticate({
        strategy: 'jwt',
        accessToken: response.data.accessToken
      });
    }

    return response.data;
  }

  async signup(credentials: SignupCredentials): Promise<any> {
    // Make sure we're sending the right data structure
    const userData = {
      email: credentials.email,
      password: credentials.password,
      displayName: credentials.displayName || ''
    };

    const response = await axios.post(`${API_URL}/users`, userData);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      // Revoke Stytch session if it exists
      const stytchSession = localStorage.getItem('stytch_session');
      if (stytchSession) {
        // Session revocation would need backend endpoint
        console.log('Stytch session logout');
      }

      localStorage.removeItem('user');
      localStorage.removeItem('stytch_session');

      // Also logout from Feathers client
      await feathers.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local storage even if API call fails
      localStorage.removeItem('user');
      localStorage.removeItem('stytch_session');
    }
  }

  getCurrentUser(): AuthResponse | null {
    const userStr = localStorage.getItem('user');
    const result = userStr ? JSON.parse(userStr) : null;
    return result;
  }

  getToken(): string | null {
    const user = this.getCurrentUser();
    const token = user ? user.accessToken : null;
    return token;
  }

  isAuthenticated(): boolean {
    const result = !!this.getCurrentUser();
    return result;
  }

  /**
   * Restore authentication on app startup if token exists
   */
  async restoreAuthentication(): Promise<{ isValid: boolean, user?: any }> {
    try {
      // Try JWT authentication
      const user = this.getCurrentUser();
      if (user && user.accessToken) {
        const authResult = await feathers.authenticate({
          strategy: 'jwt',
          accessToken: user.accessToken
        });
        
        // Update local storage with fresh user data
        if (authResult.user) {
          const updatedUserStr = JSON.stringify({
            ...user,
            user: authResult.user
          });
          localStorage.setItem('user', updatedUserStr);
        }

        return { isValid: true, user: authResult.user };
      }

      return { isValid: false };
    } catch (error) {
      // Clear invalid token
      this.logout();
      return { isValid: false };
    }
  }

  /**
   * Get Stytch session token
   */
  getStytchSession(): string | null {
    return localStorage.getItem('stytch_session');
  }

  /**
   * Check if using Stytch authentication
   */
  isStytchAuthenticated(): boolean {
    return !!this.getStytchSession();
  }
}

export default new AuthService();